import { FC, useEffect, useState } from 'react';

import { Button, Icon, Stack, useToast } from '@eluve/components';
import { getAudioConstraints } from '@eluve/mic-utils';
import {
  MicrophoneDeviceSelector,
  MicrophoneLevel,
  useSelectedMicrophoneDevice,
} from '@eluve/smart-blocks';

export const MicSettings: FC = () => {
  const toast = useToast();
  const [selectedMicId, setSelectedMicId] = useSelectedMicrophoneDevice();
  const [enableMicTest, setEnableMicTest] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

  useEffect(
    function runMicTest() {
      if (!enableMicTest) {
        return;
      }

      navigator.mediaDevices
        .getUserMedia(getAudioConstraints(selectedMicId ?? undefined))
        .then((stream) => {
          setMediaStream(stream);
          window.eluveMediaStreams.set('microphoneTester', stream);
        })
        .catch(() => {
          setMediaStream(null);
        });

      return () => {
        window.eluveMediaStreams.stop('microphoneTester');
      };
    },
    [selectedMicId, enableMicTest],
  );

  return (
    <Stack gap={2} direction="horizontal">
      <MicrophoneDeviceSelector
        onSelectedDeviceChange={(device) => {
          if (device) {
            toast.success('Microphone preferences changed');
          } else {
            toast.warning('Microphone could not be found');
          }
          setSelectedMicId(device?.deviceId ?? null);
        }}
        selectedId={selectedMicId}
      />
      {enableMicTest && (
        <MicrophoneLevel
          stream={mediaStream}
          key={selectedMicId}
          className="w-max"
          variant="compact"
        />
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setEnableMicTest(!enableMicTest)}
      >
        {enableMicTest ? (
          <>
            <Icon name="StopCircle" size="auto" />
            Stop
          </>
        ) : (
          <>
            <Icon name="Play" size="auto" />
            Test
          </>
        )}
      </Button>
    </Stack>
  );
};
